import './App.css';
import './styles/platform-theme.css';
import BootstrapNavbar from './Components/BootstrapNavbar'
import Login from './routes/login';
import About from './routes/about';
import EventRegistration from './routes/eventRegistration';
import EventAccess from './routes/eventAccess';
import MyEvents from './routes/myEvents';
import Verify from './routes/verify';
import AuthVerify from './routes/authVerify';
import UserList from './routes/userList';
import EditUser from './routes/editUser';
import Course from './routes/course';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ScrollToTop from './Components/ScrollToTop';
import ErrorReporter from './routes/ErrorReporter';
import ErrorReportButton from './Components/ErrorReportButton';
import ResetPassword from './routes/resetPassword';
import RequestReset from './routes/requestReset';
import Stats from './routes/stats';
import FooterGate from './Components/FooterGate';
import { ApplicationProvider } from './context/ApplicationContext';
import EventsList from './routes/eventsList';
import CreateEvent from './routes/createEvent';
import ModifyEvent from './routes/modifyEvent';
import EventView from './routes/eventView';
import DashboardRoutes from './routes/dashboard';
import PlatformHome from './routes/platformHome';
import Ottieni from './routes/ottieni';
import LegalPolicy from './routes/legalPolicy';
import { extractAppSlugFromPath, rememberAppSlug, resolveAppSlug } from './utils/appSlug';
import { getStoredLoginStatus } from './utils/session';
import apiService from './services/api.service';
import { CookieConsentProvider } from './context/CookieConsentContext';
import ConsentDiagnostics from './Components/ConsentDiagnostics';
import { tenantRoute, tenantSessionAllowed } from './utils/tenant';

function HostGate({ loginStatus, setLoginStatus }) {
  const [context, setContext] = useState(null);
  const [failed, setFailed] = useState(false);
  const location = useLocation();
  useEffect(() => {
    let active = true;
    apiService.getPlatformContext().then(({ data }) => {
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'application') ||
          (data.application !== null && !data.application?.slug)) throw new Error('Invalid host context');
      if (active) setContext(data);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  const pathSlug = extractAppSlugFromPath(location.pathname);
  const tenant = context?.application || (pathSlug ? { slug: pathSlug } : null);
  // A personal invitation may deliberately switch participant identity. Preserve
  // the current session until the access endpoint has validated the invitation.
  const isEventAccess = /^\/[^/]+\/event-access\/?$/.test(location.pathname);
  const sessionAllowed = isEventAccess || tenantSessionAllowed(loginStatus, tenant);
  useEffect(() => {
    if (context && !sessionAllowed) {
      localStorage.removeItem('user');
      localStorage.removeItem('authError');
      localStorage.removeItem('lastAppSlug');
      setLoginStatus({});
    }
  }, [context, sessionAllowed, setLoginStatus]);

  if (failed) return <p role="alert">Sito non disponibile.</p>;
  if (!context || !sessionAllowed) return <p role="status">Caricamento...</p>;
  const route = tenantRoute(location.pathname, context.application, location.search + location.hash);
  if (route.blocked) return <p role="alert">Pagina non disponibile su questo sito.</p>;
  if (route.redirect) return <Redirect to={route.redirect} />;
  return <AppContent loginStatus={loginStatus} setLoginStatus={setLoginStatus} hostApplication={context.application} />;
}

function AppContent({ loginStatus, setLoginStatus, hostApplication }) {
  const location = useLocation();
  const appSlug = hostApplication?.slug || resolveAppSlug(location.pathname, loginStatus);
  const pathSlug = extractAppSlugFromPath(location.pathname);

  if (hostApplication?.slug) {
    rememberAppSlug(hostApplication.slug);
  } else if (pathSlug) {
    rememberAppSlug(pathSlug);
  } else if (loginStatus?.application_slug) {
    rememberAppSlug(loginStatus.application_slug);
  }

  return (
    <CookieConsentProvider scope={appSlug || 'platform'}>
    <ConsentDiagnostics />
    <ApplicationProvider slug={appSlug}>
      <ScrollToTop />
      <div className="app-shell">
        <ErrorReportButton />
        <BootstrapNavbar loginStatus={loginStatus} setLoginStatus={setLoginStatus} />
        <main className="app-main">
          <Switch>
            <Route exact path="/" render={() => <PlatformHome loginStatus={loginStatus} />} />
            <Route exact path="/ottieni" component={Ottieni} />
            <Route exact path="/privacy-policy" render={() => <LegalPolicy kind="privacy" />} />
            <Route exact path="/cookie-policy" render={() => <LegalPolicy kind="cookies" />} />
            <Route exact path="/about"><Redirect to="/" /></Route>
            <Route path="/dashboard" render={() => <DashboardRoutes loginStatus={loginStatus} setLoginStatus={setLoginStatus} />} />
            <Route exact path="/login" render={() => <Login loginStatus={loginStatus} setLoginStatus={setLoginStatus} />} />
            <Route path="/register"><Redirect to="/login" /></Route>
            <Route path="/verify" component={Verify} />
            <Route path="/users/:id/edit" component={EditUser} />
            <Route path="/users" component={UserList} />
            <Route path="/error" component={ErrorReporter} />
            <Route path="/resetpassword" component={ResetPassword} />
            <Route path="/requestreset"><Redirect to="/login" /></Route>
            <Route path="/stats" component={Stats} />
            <Route path="/events" exact component={EventsList} />
            <Route path="/events/new" component={CreateEvent} />
            <Route path="/events/:id/edit" component={ModifyEvent} />
            <Route path="/events/:id/view" component={EventView} />
            <Route path="/:appSlug/course/:id" render={(props) => <Course loginStatus={loginStatus} appSlug={props.match.params.appSlug} />} />
            <Route exact path="/:appSlug/my-events" render={(props) => <MyEvents loginStatus={loginStatus} appSlug={props.match.params.appSlug} />} />
            <Route exact path="/:appSlug/event-access" render={(props) => <EventAccess setLoginStatus={setLoginStatus} appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug/verify" render={(props) => <Verify appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug/login" render={(props) => <Login loginStatus={loginStatus} setLoginStatus={setLoginStatus} appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug/register" render={(props) => <EventRegistration loginStatus={loginStatus} appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug/requestreset" render={(props) => <RequestReset appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug/resetpassword" render={(props) => <ResetPassword appSlug={props.match.params.appSlug} />} />
            <Route path="/:appSlug" exact render={(props) => <About loginStatus={loginStatus} appSlug={props.match.params.appSlug} />} />
          </Switch>
        </main>
        {!/^\/[^/]+\/event-access\/?$/.test(location.pathname) && <AuthVerify setLoginStatus={setLoginStatus} loginStatus={loginStatus} />}
        <FooterGate />
      </div>
    </ApplicationProvider>
    </CookieConsentProvider>
  );
}

function App() {
  const [loginStatus, setLoginStatus] = useState(getStoredLoginStatus);
  return (
    <div className='content'>
      <Router>
        <HostGate loginStatus={loginStatus} setLoginStatus={setLoginStatus} />
      </Router>
    </div>
  );
}

export default App;
